/**
 * Backup cryptography (plan §69/§127/§149): authenticated AES-GCM with a
 * PBKDF2-derived per-workspace key, a unique 96-bit nonce per chunk encryption,
 * and SHA-256 integrity over both chunks and the whole record set.
 *
 * Browser WebCrypto only. Requires a secure context (HTTPS / installed PWA);
 * crypto.subtle is undefined over plain HTTP.
 */

const encoder = new TextEncoder()

/** Crockford base32 folding so a mistyped O/I/L still derives the same key. */
export function normalizeSecret(secret: string): string {
  return secret
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1')
}

function subtle(): SubtleCrypto {
  const c = globalThis.crypto
  if (!c?.subtle) {
    throw new Error('WebCrypto is unavailable. Backup needs a secure (HTTPS) context.')
  }
  return c.subtle
}

export function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n)
  globalThis.crypto.getRandomValues(b)
  return b
}

export function toB64(bytes: Uint8Array): string {
  let s = ''
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
  return btoa(s)
}

export function fromB64(b64: string): Uint8Array {
  const s = atob(b64)
  const b = new Uint8Array(s.length)
  for (let i = 0; i < s.length; i++) b[i] = s.charCodeAt(i)
  return b
}

export function encodeText(text: string): Uint8Array {
  return encoder.encode(text)
}

export function decodeText(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes)
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await subtle().digest('SHA-256', bytes as BufferSource)
  return [...new Uint8Array(digest)].map((x) => x.toString(16).padStart(2, '0')).join('')
}

export async function deriveKey(secret: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const baseKey = await subtle().importKey('raw', encodeText(normalizeSecret(secret)) as BufferSource, 'PBKDF2', false, [
    'deriveKey',
  ])
  return subtle().deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

/** Encrypt one bounded chunk with a fresh unique nonce (never reuse under a key). */
export async function encryptChunk(
  key: CryptoKey,
  plaintext: Uint8Array,
): Promise<{ nonce: Uint8Array; ciphertext: Uint8Array }> {
  const nonce = randomBytes(12)
  const buf = await subtle().encrypt({ name: 'AES-GCM', iv: nonce as BufferSource }, key, plaintext as BufferSource)
  return { nonce, ciphertext: new Uint8Array(buf) }
}

/** Decrypt a chunk; AES-GCM authentication throws on any tampering or wrong key. */
export async function decryptChunk(key: CryptoKey, nonce: Uint8Array, ciphertext: Uint8Array): Promise<Uint8Array> {
  const buf = await subtle().decrypt({ name: 'AES-GCM', iv: nonce as BufferSource }, key, ciphertext as BufferSource)
  return new Uint8Array(buf)
}
